import generalCatalogsService from '@/services/generalCatalogs';
import suppliersService from '@/services/suppliers';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import {
  closeExpenseModalState,
  openExpenseModalState,
  submitExpenseFlow,
} from '@/composables/expenses/useExpenseFlow';
import {
  closeFeeModalState,
  openFeeModalState,
  submitFeeFlow,
} from '@/composables/fees/useFeeFlow';
import {
  createDefaultSaleExpensesFilters,
  createDefaultSaleFeesFilters,
  createInitialExpenseSummary,
  createInitialFeeSummary,
} from '@/composables/sales/saleDetail/state';

export const supplierDetailCoreMethods = {
  handlePrimaryHeaderAction(action, onAction) {
    this.showMoreActionsMenu = false;
    onAction(action);
  },
  toggleMoreActionsMenu() {
    this.showMoreActionsMenu = !this.showMoreActionsMenu;
  },
  handleSecondaryHeaderAction(action, onAction) {
    this.showMoreActionsMenu = false;
    onAction(action);
  },
  handleHeaderDocumentClick(event) {
    const target = event?.target;
    if (target && target.closest('.app-detail-more-actions-wrap')) {
      return;
    }

    this.showMoreActionsMenu = false;
  },
  openExpenseModal(mode = 'create', initialData = null) {
    openExpenseModalState(this, mode, initialData);
  },
  closeExpenseModal() {
    closeExpenseModalState(this);
  },
  openFeeModal(mode = 'create', initialData = null) {
    openFeeModalState(this, mode, initialData);
  },
  closeFeeModal() {
    closeFeeModalState(this);
  },
  async submitExpense(payload) {
    await submitExpenseFlow(this, payload, {
      saveErrorMessage: this.text.detail.expense.toast.saveError,
      createSuccessMessage: this.text.detail.expense.toast.createSuccess,
      updateSuccessMessage: this.text.detail.expense.toast.updateSuccess,
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  async submitFee(payload) {
    await submitFeeFlow(this, payload, {
      saveErrorMessage: this.text.detail.fee.toast.saveError,
      createSuccessMessage: this.text.detail.fee.toast.createSuccess,
      updateSuccessMessage: this.text.detail.fee.toast.updateSuccess,
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  buildExpenseCreateInitialData() {
    const supplierId = Number(this.supplierId || 0);
    if (supplierId <= 0) {
      return null;
    }

    return {
      supplier_id: supplierId,
      provider_id: supplierId,
      supplier: {
        id: supplierId,
        name: this.supplierName,
      },
      area_id: Number(this.supplier?.area?.id || 0) || 0,
    };
  },
  buildFeeCreateInitialData() {
    const supplierId = Number(this.supplierId || 0);
    if (supplierId <= 0) {
      return null;
    }

    return {
      commission_type: 'supplier',
      supplier_id: supplierId,
      provider_id: supplierId,
      supplier: {
        id: supplierId,
        name: this.supplierName,
      },
      area_id: Number(this.supplier?.area?.id || 0) || 0,
    };
  },
  resolveDetailPayload(response) {
    const payload = extractPayload(response);
    if (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object') {
      return payload.data;
    }

    return payload && typeof payload === 'object' ? payload : null;
  },
  async fetchSupplierDetail() {
    if (this.supplierId <= 0) {
      this.supplier = null;
      this.supplierOverview = null;
      this.persons = [];
      this.contributors = [];
      this.resetSupplierExpenseRuntime();
      this.resetSupplierFeeRuntime();
      return;
    }

    this.loading = true;

    try {
      const [detailResult, overviewResult, personsResult, contributorsResult] = await Promise.allSettled([
        suppliersService.show(this.supplierId),
        suppliersService.detailOverview(this.supplierId),
        suppliersService.personsIndex(this.supplierId),
        suppliersService.contributorsIndex(this.supplierId),
      ]);

      if (detailResult.status === 'fulfilled') {
        this.supplier = this.resolveDetailPayload(detailResult.value);
      } else {
        this.supplier = null;
      }

      if (overviewResult.status === 'fulfilled') {
        this.supplierOverview = this.resolveDetailPayload(overviewResult.value);
      } else {
        this.supplierOverview = null;
      }

      if (personsResult.status === 'fulfilled') {
        this.persons = extractCollection(personsResult.value);
      } else {
        this.persons = [];
      }

      if (contributorsResult.status === 'fulfilled') {
        this.contributors = extractCollection(contributorsResult.value);
      } else {
        this.contributors = [];
      }

      if (this.activeTab === 'operation') {
        await Promise.allSettled([
          this.fetchSupplierExpenses(),
          this.fetchSupplierFees(),
        ]);
      }
    } catch (error) {
      this.supplier = null;
      this.supplierOverview = null;
      this.persons = [];
      this.contributors = [];
      this.resetSupplierExpenseRuntime();
      this.resetSupplierFeeRuntime();
    } finally {
      this.loading = false;
    }
  },
  openSupplierEditModal(sectionKey = 'general') {
    if (!this.supplier) {
      return;
    }

    this.supplierEditSectionKey = String(sectionKey || 'general').trim() || 'general';
    this.isSupplierEditModalOpen = true;
  },
  closeSupplierEditModal(force = false) {
    if (this.isSupplierSubmitting && !force) {
      return;
    }

    this.isSupplierEditModalOpen = false;
    this.supplierEditSectionKey = 'general';
  },
  async submitSupplierEdit(payload) {
    if (this.isSupplierSubmitting || this.supplierId <= 0) {
      return;
    }

    const supplierPayload = payload?.payload || {};
    const specialtyIds = Array.isArray(payload?.specialtyIds) ? payload.specialtyIds : [];

    this.isSupplierSubmitting = true;
    try {
      const response = await suppliersService.update(this.supplierId, supplierPayload);

      try {
        await suppliersService.skillsStore(this.supplierId, {
          skills: specialtyIds,
          replace: true,
        });
      } catch (skillsError) {
        this.$toast?.error(this.text.toast.specialtiesSyncWarning);
      }

      this.closeSupplierEditModal(true);
      await this.fetchSupplierDetail();
      this.$toast?.success(response?.data?.message || response?.message || this.text.detail.toast.updateSuccess);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || this.text.detail.toast.saveError;
      this.$toast?.error(message);
    } finally {
      this.isSupplierSubmitting = false;
    }
  },
  async handleDeleteSupplierFromModal() {
    if (this.supplierId <= 0) {
      return;
    }

    this.closeSupplierEditModal(true);
    this.pendingActionType = 'delete-supplier';
    this.pendingActionSupplier = { ...(this.supplier || {}) };
    this.isConfirmActionModalOpen = true;
  },
  handleDeleteSupplierFromAdvancedOptions() {
    if (this.supplierId <= 0) {
      return;
    }

    this.pendingActionType = 'delete-supplier';
    this.pendingActionSupplier = { ...(this.supplier || {}) };
    this.isConfirmActionModalOpen = true;
  },
  handleArchiveSupplierToggle() {
    if (this.supplierId <= 0) {
      return;
    }

    const isActive = Number(this.supplier?.active || 0) === 1;

    this.pendingActionType = isActive ? 'archive-supplier' : 'unarchive-supplier';
    this.pendingActionSupplier = { ...(this.supplier || {}) };
    this.isConfirmActionModalOpen = true;
  },
  closeConfirmActionModal() {
    this.isConfirmActionModalOpen = false;
    this.pendingActionType = '';
    this.pendingActionSupplier = null;
  },
  openSupplierMergeModal() {
    if (this.supplierId <= 0) {
      return;
    }

    this.isMergeModalOpen = true;
  },
  closeSupplierMergeModal(force = false) {
    if (this.mergeSubmitting && !force) {
      return;
    }

    this.isMergeModalOpen = false;
  },
  async submitSupplierMerge(payload) {
    if (this.supplierId <= 0) {
      return;
    }

    this.mergeSubmitting = true;

    try {
      const response = await suppliersService.merge(this.supplierId, payload || {});
      this.$toast?.success(response?.data?.message || response?.message || this.text.detail.toast.mergeSuccess);
      this.closeSupplierMergeModal(true);
      await this.fetchSupplierDetail();
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || this.text.detail.toast.mergeError;
      this.$toast?.error(message);
    } finally {
      this.mergeSubmitting = false;
    }
  },
  async confirmPendingAction() {
    if (!this.pendingActionType || this.supplierId <= 0) {
      this.closeConfirmActionModal();
      return;
    }

    try {
      if (this.pendingActionType === 'archive-supplier' || this.pendingActionType === 'unarchive-supplier') {
        const nextActive = this.pendingActionType === 'unarchive-supplier' ? 1 : 0;
        const response = await suppliersService.toggleStatus(this.supplierId, { active: nextActive });
        this.$toast?.success(response?.data?.message || response?.message || this.text.toast.toggleStatusSuccess);
        this.closeConfirmActionModal();
        await this.fetchSupplierDetail();
        return;
      }

      const response = await suppliersService.delete(this.supplierId);
      this.$toast?.success(response?.data?.message || response?.message || this.text.detail.toast.deleteSuccess);
      this.closeConfirmActionModal();
      this.$router.push({ name: 'suppliers' }).catch(() => {});
    } catch (error) {
      const fallback = this.pendingActionType === 'delete-supplier'
        ? this.text.detail.toast.deleteError
        : this.text.detail.toast.statusUpdateError;
      const message = error?.response?.data?.message || error?.message || fallback;
      this.$toast?.error(message);
    }
  },
  handleEditSection(sectionKey) {
    this.openSupplierEditModal(sectionKey);
  },
  async ensureGeoCatalogsLoaded() {
    if (this.countryOptions.length > 0 && this.stateOptions.length > 0) {
      return;
    }

    const [countriesResult, statesResult] = await Promise.allSettled([
      generalCatalogsService.getCountries(),
      generalCatalogsService.getStates(),
    ]);

    if (countriesResult.status === 'fulfilled') {
      this.countryOptions = normalizeCatalogOptions(extractCollection(countriesResult.value));
    }

    if (statesResult.status === 'fulfilled') {
      this.stateOptions = normalizeCatalogOptions(extractCollection(statesResult.value));
    }
  },
  openHistoryTab() {
    this.activeTab = 'history';
  },
  onHeaderAction(action) {
    const key = String(action?.key || '').trim();

    if (key === 'edit-supplier') {
      this.openSupplierEditModal('general');
      return;
    }

    if (key === 'new-expense') {
      this.openExpenseModal('create', this.buildExpenseCreateInitialData());
      return;
    }

    if (key === 'new-commission') {
      this.openFeeModal('create', this.buildFeeCreateInitialData());
    }
  },
  resetSupplierOperationRuntime() {
    this.expenseRows = [];
    this.expenseSummary = createInitialExpenseSummary();
    this.expenseCurrentPage = 1;
    this.expenseActiveStatusTab = 'all';
    this.expenseFilters = createDefaultSaleExpensesFilters();
    this.expenseSearchText = '';
    this.expenseInFlightRequestKey = '';

    this.feeRows = [];
    this.feeSummary = createInitialFeeSummary();
    this.feeCurrentPage = 1;
    this.feeActiveStatusTab = 'all';
    this.feeFilters = createDefaultSaleFeesFilters();
    this.feeSearchText = '';
    this.feeInFlightRequestKey = '';
  },
};
